import React from 'react';
import { Target, Brain, Heart, Settings as Lungs, Stethoscope, Activity, Baby, Zap, ShieldCheck, ListChecks, Building2, Microscope } from 'lucide-react';
import './Ts42.css';

const Ts42: React.FC = () => {
  return (
    <div className="ts42">
      {/* Hero Section */}
      <div className="hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Proton Therapy Treatment</h1>
          <p>Advanced Cancer Treatment with Precision and Care</p>
        </div>
      </div>

      {/* Introduction Section */}
      <section className="intro-section container">
        <div className="intro-content">
          <div className="intro-text">
            <h2>Understanding Proton Therapy</h2>
            <p>
              Proton therapy represents a significant advancement in radiation treatment, 
              offering precise targeting of cancerous cells while minimizing damage to 
              surrounding healthy tissues. Unlike traditional radiation methods, proton 
              therapy delivers maximum energy directly to the tumor site.
            </p>
          </div>
          <div className="video-container">
            <iframe
              src="https://www.youtube.com/embed/399OlHiC4AE"
              title="What is Proton Therapy?"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="advantages-section container">
        <h2 className="section-title">Advantages of Proton Therapy</h2>
        <div className="advantages-grid">
          <div className="advantage-card">
            <Target className="card-icon" />
            <h3>Precise Targeting</h3>
            <p>Controlled proton beams stop precisely at the tumor site, minimizing exposure to healthy tissues.</p>
          </div>
          <div className="advantage-card">
            <ShieldCheck className="card-icon" />
            <h3>Reduced Side Effects</h3>
            <p>Significantly less damage to surrounding healthy tissues compared to traditional radiation.</p>
          </div>
          <div className="advantage-card">
            <Activity className="card-icon" />
            <h3>Better Cancer Control</h3>
            <p>Increased effectiveness with fewer complications due to precise energy delivery.</p>
          </div>
        </div>
      </section>

      {/* Treatment Types Section */}
      <section className="treatments-section">
        <div className="container">
          <h2 className="section-title">Cancer Types Treated</h2>
          <div className="treatments-grid">
            <div className="treatment-card">
              <Brain className="card-icon" />
              <h3>Brain Tumors</h3>
              <p>Reduces radiation to normal brain tissue, preserving memory and endocrine function.</p>
              <ul className="treatment-list">
                <li>Unresectable tumors</li>
                <li>Arteriovenous hemangiomas</li>
                <li>Acoustic neuromas</li>
                <li>Pituitary tumors</li>
              </ul>
            </div>

            <div className="treatment-card">
              <Stethoscope className="card-icon" />
              <h3>Head and Neck Cancer</h3>
              <p>Minimizes radiation to oral cavity and throat, preserving taste and reducing side effects.</p>
            </div>

            <div className="treatment-card">
              <Lungs className="card-icon" />
              <h3>Lung Cancer</h3>
              <p>Reduces radiation to heart and healthy lung tissue, minimizing respiratory complications.</p>
              <ul className="treatment-list">
                <li>Stage 1 to stage 3</li>
                <li>Localized tumors</li>
              </ul>
            </div>

            <div className="treatment-card">
              <Heart className="card-icon" />
              <h3>Breast Cancer</h3>
              <p>Minimizes radiation exposure to heart and lungs, reducing long-term cardiac risks.</p>
            </div>

            <div className="treatment-card">
              <Microscope className="card-icon" />
              <h3>Liver Cancer</h3>
              <p>Delivers precise high-dose radiation with excellent local control rates.</p>
            </div>

            <div className="treatment-card">
              <Baby className="card-icon" />
              <h3>Pediatric Tumors</h3>
              <p>Reduces impact on growth and development, lowering secondary tumor risks.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="technology-section container">
        <div className="tech-content">
          <div className="tech-info">
            <h2>Advanced Treatment Technology</h2>
            <p>
              Our proton therapy system utilizes state-of-the-art technology to deliver 
              precise radiation treatment. The system's advanced imaging and targeting 
              capabilities ensure accurate tumor targeting while sparing healthy tissue.
            </p>
            <div className="tech-features">
              <div className="tech-feature">
                <Zap className="feature-icon" />
                <span>Precise Beam Control</span>
              </div>
              <div className="tech-feature">
                <ListChecks className="feature-icon" />
                <span>Real-time Tracking</span>
              </div>
              <div className="tech-feature">
                <Building2 className="feature-icon" />
                <span>State-of-the-art Facility</span>
              </div>
            </div>
          </div>
          <div className="tech-image">
            <img 
              src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&q=80&w=2000"
              alt="Advanced Medical Technology"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Ts42;